import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { GET_MATCH_DETAILS } from "../../../GraphQL/matches/queries";
import { UPDATE_MATCH_STATUS } from "../../../GraphQL/matches/mutations";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { currencyFormat } from "../../../utils";
import { MatchStatus, MatchDetailsData, MatchStatusHistoryType, PublicCandidate, PublicJob } from "../../../interfaces";
import PublicCandidateDetailModal from "../../PublicCandidates/PublicCandidateDetailModal";
import PublicRoleDetailModal from "../../PublicRoles/PublicRoleDetailModal";
import { StatusBadge, getStatusVariant } from "@/components/ui/styled-table";
import { formatStatusLabel } from "@/utils/matchStatus";

interface Props {
  show: boolean;
  onHide: () => void;
  onRefresh?: () => void;
  matchContent: any;
  tabType: string;
}

const MatchesDetailModal = ({ show, onHide, onRefresh, matchContent, tabType }: Props) => {
  const matchId = matchContent?.id;

  const {
    error: matchDetailsError,
    loading: matchDetailsLoading,
    data: matchDetailsData,
  } = useQuery<MatchDetailsData>(GET_MATCH_DETAILS, {
    variables: matchId ? { id: matchId } : {},
    skip: !matchId,
  });

  const match = matchDetailsData?.matchDetails;

  // Candidate and role data are now flattened in the match object
  const candidate = match ? {
    id: match.candidateId,
    firstName: match.candidateName.split(' ')[0] || '',
    lastName: match.candidateName.split(' ')[1] || '',
    jobTitle: match.candidateJobTitle,
    idealJobTitles: match.candidateIdealJobTitles,
    location: match.candidateLocation,
    summary: '', // Not available in flattened structure
    salaryMin: 0, // Not available in flattened structure
    salaryMax: 0, // Not available in flattened structure
    workType: 'PERMANENT' as any, // Default value
    industry: '', // Not available in flattened structure
    yearsOfExperience: 0, // Not available in flattened structure
    currency: match.currency
  } : undefined;

  const role = match ? {
    id: match.jobId,
    title: match.jobTitle,
    summary: match.jobSummary,
    salaryMin: match.jobSalaryMin,
    salaryMax: match.jobSalaryMax,
    location: match.jobLocation,
    clientName: match.jobClientName,
    workType: 'PERMANENT' as any, // Default value
    currency: match.currency
  } : undefined;

  const [newStatus, setNewStatus] = useState<MatchStatus>("NEW");
  const [statusNotes, setStatusNotes] = useState<string>("");
  const [showStatusForm, setShowStatusForm] = useState<boolean>(false);
  const [showPublicCandidateModal, setShowPublicCandidateModal] = useState<boolean>(false);
  const [showPublicRoleModal, setShowPublicRoleModal] = useState<boolean>(false);
  const [didUpdate, setDidUpdate] = useState<boolean>(false);

  // State to control whether to show all history or just the last 5
  const [showAllHistory, setShowAllHistory] = useState<boolean>(false);

  const [updateMatchStatus] = useMutation(UPDATE_MATCH_STATUS, {
    refetchQueries: [
      { query: GET_MATCH_DETAILS, variables: { id: matchId } }
    ],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setShowStatusForm(false);
      setStatusNotes("");
      onRefresh?.();
    },
    onError: (err) => {
      console.error("updateMatchStatus error:", err);
    }
  });

  // Convert match data to the format expected by the public detail modals
  const publicCandidate: PublicCandidate | null = candidate ? {
    id: candidate.id,
    firstName: candidate.firstName,
    lastName: candidate.lastName,
    jobTitle: candidate.jobTitle || '',
    idealJobTitles: candidate.idealJobTitles || '',
    location: {
      displayName: candidate.location?.displayName || '',
      localityId: candidate.location?.localityId || 0
    },
    summary: candidate.summary || '',
    salaryMin: candidate.salaryMin || 0,
    salaryMax: candidate.salaryMax || 0,
    workType: candidate.workType,
    industry: candidate.industry || '',
    yearsOfExperience: (candidate.yearsOfExperience || 0).toString(),
    currency: candidate.currency || '',
    interviewed: false,
    status: 'SHARED' as any,
    companyName: '',
    externalId: match?.candidateId?.toString() || '',
    communities: []
  } : null;

  const publicRole: PublicJob | null = role ? {
    id: role.id,
    externalId: match?.jobId?.toString() || '',
    title: role.title || '',
    summary: role.summary || '',
    salaryMin: role.salaryMin || 0,
    salaryMax: role.salaryMax || 0,
    location: {
      displayName: role.location?.displayName || '',
      localityId: role.location?.localityId || 0
    },
    companyName: role.clientName || '',
    workType: role.workType,
    currency: role.currency || '',
    industry: '',
    startDate: new Date().toISOString(),
    placementStartDate: new Date().toISOString(),
    placementEndDate: new Date().toISOString(),
    workingHours: '',
    engagementType: 'PERMANENT' as any,
    termsSigned: false,
    replacementGuarantee: false,
    status: 'SHARED' as any,
    feeMin: 0,
    feeMax: 0,
    communities: []
  } : null;

  // Render the public detail modals alongside the main modal
  const detailModals = (
    <>
      {publicCandidate && (
        <PublicCandidateDetailModal
          variantType="public"
          show={showPublicCandidateModal}
          onHide={() => setShowPublicCandidateModal(false)}
          candidate={publicCandidate}
          hideMatchingSection={true}
        />
      )}

      {publicRole && (
        <PublicRoleDetailModal
          show={showPublicRoleModal}
          onHide={() => setShowPublicRoleModal(false)}
          role={publicRole}
          hideMatchingSection={true}
        />
      )}
    </>
  );

  //  Helper function to get the display history (last 5 or all)
  const getDisplayHistory = () => {
    if (!match?.statusHistory) return [];
    return showAllHistory ? match.statusHistory : match.statusHistory.slice(0, 5);
  };

  if (tabType === "roles-matches") {
    return (
      <>
        <Dialog
          open={show}
          onOpenChange={(open) => {
            if (!open) {
              onHide();
              if (didUpdate) {
                onRefresh?.();
                setDidUpdate(false);
              }
            }
          }}
        >
          <DialogContent className="max-w-6xl">
            <DialogHeader>
              <DialogTitle>
                {matchContent?.clientName} - {matchContent?.jobTitle}
              </DialogTitle>
            </DialogHeader>
            <div>
            {matchDetailsLoading && <p>Loading...</p>}
            {matchDetailsError && <p>Error: {matchDetailsError?.message}</p>}
            {!matchDetailsError && !matchDetailsLoading && matchDetailsData && (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                {/* Left Column - Candidate and Role Details  */}
                <div className="md:col-span-5">
                  <div className="max-h-[60vh] overflow-y-auto pr-2.5">
                    {/* Candidate Details */}
                    <h5 className="mb-3">Candidate Details</h5>
                    <div className="mb-3">
                      <strong>Name:</strong> {match?.candidateName}<br />
                      <strong>Job Title:</strong> {candidate?.jobTitle}<br />
                      <strong>Ideal Job Titles:</strong> {candidate?.idealJobTitles}<br />
                      <strong>Location:</strong> {candidate?.location?.displayName}<br />
                      <strong>Work Type:</strong> {candidate?.workType === "TEMP" ? "CONTRACT/TEMP" : "PERMANENT"}<br />
                      <strong>Salary:</strong> {currencyFormat(candidate?.salaryMin || 0)} -{" "}
                      {currencyFormat(candidate?.salaryMax || 0)} {candidate?.currency}<br />
                    </div>

                    {/* Button to open Candidate Details Modal */}
                    <div className="mb-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPublicCandidateModal(true)}
                      >
                        Show Candidate Details
                      </Button>
                    </div>

                    <hr />

                    {/* Role Details */}
                    <h5 className="mb-3 mt-4">Role Details</h5>
                    <div className="mb-3">
                      <strong>Job Title:</strong> {role?.title}<br />
                      <strong>Client:</strong> {role?.clientName}<br />
                      <strong>Location:</strong> {role?.location?.displayName}<br />
                      <strong>Work Type:</strong> {role?.workType === "TEMP" ? "CONTRACT/TEMP" : "PERMANENT"}<br />
                      {role?.workType === "PERMANENT" && (
                        <>
                          <strong>Salary:</strong> {currencyFormat(role?.salaryMin || 0)} -{" "}
                          {currencyFormat(role?.salaryMax || 0)} {role?.currency}<br />
                        </>
                      )}
                      {role?.workType === "TEMP" && (
                        <>
                          <strong>Rate:</strong> {currencyFormat(role?.salaryMin || 0)} -{" "}
                          {currencyFormat(role?.salaryMax || 0)} per hr<br />
                        </>
                      )}
                    </div>


                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPublicRoleModal(true)}
                      >
                        Show Role Details
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Right Column - Status History  */}
                <div className="md:col-span-7">
                  <Card className="max-h-[60vh] overflow-hidden flex flex-col">
                    <CardHeader className="flex justify-between items-center">
                      <h5 className="mb-0">Match Status</h5>
                      {!showStatusForm && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowStatusForm(true)}
                        >
                          Update Status
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent className="flex flex-col" style={{ height: "calc(540px - 60px)" }}>

                      {!showStatusForm && (
                        <>

                          {match?.statusHistory && match.statusHistory.length > 0 ? (
                            <>
                              <div className="flex-1" style={{ overflowY: "auto", minHeight: 0 }}>
                                {getDisplayHistory().map((history: MatchStatusHistoryType) => (
                                  <Card key={history.id} className="border-0 bg-muted/30 mb-2">
                                    <CardContent className="p-2">
                                      <div className="flex justify-between items-center mb-1">
                                        <StatusBadge $variant={getStatusVariant(history.status)} className="text-sm">
                                          {formatStatusLabel(history.status)}
                                        </StatusBadge>
                                        <small className="text-muted-foreground">
                                          {new Date(history.updatedAt).toLocaleDateString()}
                                        </small>
                                      </div>
                                      {history.notes && (
                                        <div className="text-sm mb-1">
                                          <strong>Notes:</strong> {history.notes}
                                        </div>
                                      )}
                                      {history.updatedByName && (
                                        <div className="text-sm text-muted-foreground">
                                          Updated by: {history.updatedByName}
                                        </div>
                                      )}
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>

                              {/* Show "Show All" / "Show Less" button if there are more than 5 items */}
                              {match.statusHistory.length > 5 && (
                                <div className="text-center mt-2">
                                  <Badge
                                    style={{
                                      cursor: 'pointer',
                                      padding: '6px 12px',
                                      fontSize: '0.875rem',
                                      backgroundColor: '#17a2b8',
                                      color: '#fff'
                                    }}
                                    onClick={() => setShowAllHistory(!showAllHistory)}
                                  >
                                    {showAllHistory ? '▲ Show Less' : `▼ Show All (${match.statusHistory.length} updates)`}
                                  </Badge>
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="text-muted-foreground">No status history available</p>
                          )}
                        </>
                      )}

                      {showStatusForm && (
                        <Card className="mt-3 border-0">
                          <CardContent className="p-3">
                            <form onSubmit={async (e) => {
                              e.preventDefault();
                              if (!matchId) return;

                              try {
                                await updateMatchStatus({
                                  variables: {
                                    id: matchId,
                                    status: newStatus,
                                    notes: statusNotes || null,
                                  },
                                });
                              } catch (err) {
                                console.error("Status update failed:", err);
                              }
                            }}>
                              <div className="space-y-2 mb-3">
                                <Label>Status</Label>
                                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                  value={newStatus}
                                  onChange={(e) => setNewStatus(e.target.value as MatchStatus)}
                                >
                                  <option value="NEW">New</option>
                                  <option value="AGENCY_REJECTED">Agency Rejected</option>
                                  <option value="ACCEPTED">Accepted</option>
                                  <option value="INTERVIEWING">Interviewing</option>
                                  <option value="SHORTLISTED">Shortlisted</option>
                                  <option value="OFFER_EXTENDED">Offer Extended</option>
                                  <option value="OFFER_ACCEPTED">Offer Accepted</option>
                                  <option value="PLACED">Placed</option>
                                  <option value="REJECTED">Rejected</option>
                                  <option value="WITHDRAWN">Withdrawn</option>
                                </select>
                              </div>
                              <div className="space-y-2 mb-3">
                                <Label>Notes (Optional)</Label>
                                <Textarea
                                  rows={3}
                                  value={statusNotes}
                                  onChange={(e) => setStatusNotes(e.target.value)}
                                  placeholder="Add any notes about this status update..."
                                />
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  type="button"
                                  onClick={() => setShowStatusForm(false)}
                                >
                                  Cancel
                                </Button>
                                <Button variant="default" type="submit" size="sm">
                                  Update Status
                                </Button>
                              </div>
                            </form>
                          </CardContent>
                        </Card>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </div></DialogContent>
        </Dialog>
        {detailModals}
      </>
    );
  } else {
    return (
      <>
        <Dialog
          open={show}
          onOpenChange={(open) => {
            if (!open) {
              onHide();
              if (didUpdate) {
                onRefresh?.();
                setDidUpdate(false);
              }
            }
          }}
        >
          <DialogContent className="max-w-6xl">
            <DialogHeader>
              <DialogTitle>
                {matchContent?.candidateName} - {matchContent?.candidateJobTitle}
              </DialogTitle>
            </DialogHeader>
            <div>
            {matchDetailsLoading && <p>Loading...</p>}
            {matchDetailsError && <p>Error: {matchDetailsError?.message}</p>}
            {!matchDetailsError && !matchDetailsLoading && matchDetailsData && (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                {/* Left Column - Candidate and Role Details (no card wrapper) */}
                <div className="md:col-span-5">
                  <div className="max-h-[60vh] overflow-y-auto pr-2.5">
                    {/* Candidate Details */}
                    <h5 className="mb-3">Candidate Details</h5>
                    <div className="mb-3">
                      <strong>Name:</strong> {match?.candidateName}<br />
                      <strong>Job Title:</strong> {candidate?.jobTitle}<br />
                      <strong>Ideal Job Titles:</strong> {candidate?.idealJobTitles}<br />
                      <strong>Location:</strong> {candidate?.location?.displayName}<br />
                      <strong>Work Type:</strong> {candidate?.workType === "TEMP" ? "CONTRACT/TEMP" : "PERMANENT"}<br />
                      <strong>Salary:</strong> {currencyFormat(candidate?.salaryMin || 0)} -{" "}
                      {currencyFormat(candidate?.salaryMax || 0)} {candidate?.currency}<br />
                    </div>

                    {/* Button to open Candidate Details Modal */}
                    <div className="mb-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPublicCandidateModal(true)}
                      >
                        Show Candidate Details
                      </Button>
                    </div>

                    <hr />

                    {/* Role Details */}
                    <h5 className="mb-3 mt-4">Role Details</h5>
                    <div className="mb-3">
                      <strong>Job Title:</strong> {role?.title}<br />
                      <strong>Client:</strong> {role?.clientName}<br />
                      <strong>Location:</strong> {role?.location?.displayName}<br />
                      <strong>Work Type:</strong> {role?.workType === "TEMP" ? "CONTRACT/TEMP" : "PERMANENT"}<br />
                      {role?.workType === "PERMANENT" && (
                        <>
                          <strong>Salary:</strong> {currencyFormat(role?.salaryMin || 0)} -{" "}
                          {currencyFormat(role?.salaryMax || 0)} {role?.currency}<br />
                        </>
                      )}
                      {role?.workType === "TEMP" && (
                        <>
                          <strong>Rate:</strong> {currencyFormat(role?.salaryMin || 0)} -{" "}
                          {currencyFormat(role?.salaryMax || 0)} per hr<br />
                        </>
                      )}
                    </div>

                    {/* Button to open Role Details Modal */}
                    <div className="mb-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowPublicRoleModal(true)}
                      >
                        Show Role Details
                      </Button>
                    </div>

                    {/* Agent Information for Successful Placements */}
                    {match && (match?.candidateAgentStatus === "AGENT_CONFIRMED" || match?.candidateAgentStatus === "SUCCESSFUL_PLACEMENT") && (
                      <div className="mt-3">
                        <hr />
                        <h6 >Agent Contact Information</h6>
                        <p>
                          <strong>Agent:</strong> {match?.candidateAgentName}<br />
                          <strong>Email:</strong> {match?.candidateAgentEmail}<br />
                          <strong>Phone:</strong> {match?.candidateAgentPhone}
                        </p>
                      </div>
                    )}

                    {match && match?.candidateAgentStatus === "SUCCESSFUL_PLACEMENT" && (
                      <div className="mt-3">
                        <h6>Placement Details</h6>
                        <p>
                          <strong>Claimed expected fee:</strong> {match?.candidateAgentExpectedFee}<br />
                          <strong>Claimed expected start date:</strong>{" "}
                          {match?.candidateAgentExpectedStartDate && new Date(match?.candidateAgentExpectedStartDate).toLocaleDateString()}<br />
                          <strong>Claimed expected schedule:</strong> {match?.candidateAgentExpectedSchedule}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Status History  */}
                <div className="md:col-span-7">
                  <Card className="max-h-[60vh] overflow-hidden flex flex-col">
                    <CardHeader className="flex justify-between items-center">
                      <h5 className="mb-0">Match Status</h5>
                      {!showStatusForm && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowStatusForm(true)}
                        >
                          Update Status
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent className="flex flex-col" style={{ height: "calc(540px - 60px)" }}>
                      {/* Status History - Hidden when status form is shown */}
                      {!showStatusForm && (
                        <>
                          {/* now using getDisplayHistory() to show limited or all history */}
                          {match?.statusHistory && match.statusHistory.length > 0 ? (
                            <>
                              <div className="flex-1" style={{ overflowY: "auto", minHeight: 0 }}>
                                {getDisplayHistory().map((history: MatchStatusHistoryType) => (
                                  <Card key={history.id} className="border-0 bg-muted/30 mb-2">
                                    <CardContent className="p-2">
                                      <div className="flex justify-between items-center mb-1">
                                        <StatusBadge $variant={getStatusVariant(history.status)} className="text-sm">
                                          {formatStatusLabel(history.status)}
                                        </StatusBadge>
                                        <small className="text-muted-foreground">
                                          {new Date(history.updatedAt).toLocaleDateString()}
                                        </small>
                                      </div>
                                      {history.notes && (
                                        <div className="text-sm mb-1">
                                          <strong>Notes:</strong> {history.notes}
                                        </div>
                                      )}
                                      {history.updatedByName && (
                                        <div className="text-sm text-muted-foreground">
                                          Updated by: {history.updatedByName}
                                        </div>
                                      )}
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>

                              {/*  Show "Show All" / "Show Less" button if there are more than 5 items */}
                              {match.statusHistory.length > 5 && (
                                <div className="text-center mt-2">
                                  <Badge
                                    style={{
                                      cursor: 'pointer',
                                      padding: '6px 12px',
                                      fontSize: '0.875rem',
                                      backgroundColor: '#17a2b8',
                                      color: '#fff'
                                    }}
                                    onClick={() => setShowAllHistory(!showAllHistory)}
                                  >
                                    {showAllHistory ? '▲ Show Less' : `▼ Show All (${match.statusHistory.length} updates)`}
                                  </Badge>
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="text-muted-foreground">No status history available</p>
                          )}
                        </>
                      )}

                      {showStatusForm && (
                        <Card className="mt-3 border-0">
                          <CardContent className="p-3">
                            <form onSubmit={async (e) => {
                              e.preventDefault();
                              if (!matchId) return;

                              try {
                                await updateMatchStatus({
                                  variables: {
                                    id: matchId,
                                    status: newStatus,
                                    notes: statusNotes || null,
                                  },
                                });
                              } catch (err) {
                                console.error("Status update failed:", err);
                              }
                            }}>
                              <div className="space-y-2 mb-3">
                                <Label>Status</Label>
                                <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                  value={newStatus}
                                  onChange={(e) => setNewStatus(e.target.value as MatchStatus)}
                                >
                                  <option value="NEW">New</option>
                                  <option value="AGENCY_REJECTED">Agency Rejected</option>
                                  <option value="ACCEPTED">Accepted</option>
                                  <option value="INTERVIEWING">Interviewing</option>
                                  <option value="SHORTLISTED">Shortlisted</option>
                                  <option value="OFFER_EXTENDED">Offer Extended</option>
                                  <option value="OFFER_ACCEPTED">Offer Accepted</option>
                                  <option value="PLACED">Placed</option>
                                  <option value="REJECTED">Rejected</option>
                                  <option value="WITHDRAWN">Withdrawn</option>
                                </select>
                              </div>
                              <div className="space-y-2 mb-3">
                                <Label>Notes (Optional)</Label>
                                <Textarea
                                  rows={3}
                                  value={statusNotes}
                                  onChange={(e) => setStatusNotes(e.target.value)}
                                  placeholder="Add any notes about this status update..."
                                />
                              </div>
                              <div className="flex gap-2 justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  type="button"
                                  onClick={() => setShowStatusForm(false)}
                                >
                                  Cancel
                                </Button>
                                <Button variant="default" type="submit" size="sm">
                                  Update Status
                                </Button>
                              </div>
                            </form>
                          </CardContent>
                        </Card>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </div></DialogContent>
        </Dialog>
        {detailModals}
      </>
    );
  }
};

export default MatchesDetailModal;